import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Info } from 'lucide-react';
import { useForm } from '../context/FormContext';
import { useAppConfig } from '../context/AppConfigContext';
import { formatPrice } from '../utils/formatters';
import { funnelOffersSupplements, getSupplementVariantIds } from '../utils/supplements';
import { scoreSupplements, ScoredSupplement } from '../utils/supplementRecommendation';
import { resolveBreedSize } from '../utils/breedSize';
import { DEFAULT_PESO_KG } from '../utils/nutritionRules';
import StepNav from '../components/funnel/StepNav';
import { Phase2Header } from '../components/funnel/Phase2Header';
import FunnelStickyCTA from '../components/funnel/FunnelStickyCTA';
import SupplementDetailModal, { supplementDisplayName } from '../components/funnel/SupplementDetailModal';

const DEFAULT_INITIAL_VISIBLE = 4;
const DEFAULT_STRONG_THRESHOLD = 15;
const PHASE2_STEP_INDEX_RECETAS = 0;
const PHASE2_STEP_INDEX_PLAN = 1;
const PHASE2_STEP_INDEX_EXTRAS = 2;
const PHASE2_STEP_INDEX_CHECKOUT = 3;
// Slots reales del FUNNEL_ORDER: Recetas=10, TuPlan=11, ComoEmpezar=12, Checkout=14.
// (Antes RECETAS=11 y PLAN=12 por un off-by-one heredado → el StepNav llevaba
// "RECETAS" a Tu Plan en vez de a Recetas.)
const FUNNEL_SLOT_RECETAS = 10;
const FUNNEL_SLOT_PLAN = 11;
const FUNNEL_SLOT_CHECKOUT = 14;
// "Atrás" desde Extras(13) vuelve a la pantalla anterior del flujo: ComoEmpezar(12).
const FUNNEL_SLOT_COMO_EMPEZAR = 12;

interface RecommendationBadgeProps {
  isRecommended: boolean;
  isStrong: boolean;
  strongLabel: string;
  label: string;
}

function RecommendationBadge({ isRecommended, isStrong, strongLabel, label }: RecommendationBadgeProps) {
  if (!isRecommended) return null;
  return (
    <span className="inline-flex items-center text-[0.55rem] sm:text-[0.65rem] font-bold px-2 sm:px-2.5 py-0.5 rounded-lg uppercase tracking-wide sm:tracking-wider whitespace-nowrap bg-[#008BBC] text-white shadow-sm">
      {isStrong ? strongLabel : label}
    </span>
  );
}

export default function Step12Extras() {
  const { formData, goToNextStep, setCurrentStep, updateFormData } = useForm();
  const { t, config, isLoading } = useAppConfig();

  const supplementRules = config.supplement_rules;
  const recommendationConfig = config.recipe_recommendation_rules;

  // Safety net: this step is skipped when the effective channel offers no
  // supplements (ComoEmpezar jumps straight to Checkout, the breadcrumb hides
  // it). If we still land here (restored draft, deep link), advance out of it.
  const supplementsOffered = funnelOffersSupplements(supplementRules);
  useEffect(() => {
    if (!supplementsOffered) goToNextStep();
  }, [supplementsOffered, goToNextStep]);
  const INITIAL_VISIBLE = supplementRules.initial_visible_count ?? DEFAULT_INITIAL_VISIBLE;
  const STRONG_RECOMMENDATION_THRESHOLD = supplementRules.strong_recommendation_threshold ?? DEFAULT_STRONG_THRESHOLD;

  const breedSize = useMemo(
    () => resolveBreedSize(formData.raza || '', formData.peso || DEFAULT_PESO_KG, recommendationConfig),
    [formData.raza, formData.peso, recommendationConfig],
  );

  const dogProfile = useMemo(
    () => ({
      ageCategory: formData.ageCategory || 'adulto',
      activityLevel: formData.activityLevel || 'medio',
      bodyCondition: formData.bodyCondition || 'ideal',
      pathologies: formData.pathologies || [],
      // Fase 2 — alérgenos declarados (proteínas + marisco/huevo/algas) para los
      // bloqueos y cautelas clínicas del scorer.
      allergens: (formData.proteinAllergies || []).filter((a) => a && a !== 'ninguna') as string[],
      subPathologies: formData.subPathologies || {},
      breedSize,
      dogName: formData.nombre || 'tu perro',
      breed: formData.raza || '',
    }),
    [
      formData.ageCategory,
      formData.activityLevel,
      formData.bodyCondition,
      formData.pathologies,
      formData.proteinAllergies,
      formData.subPathologies,
      breedSize,
      formData.nombre,
      formData.raza,
    ],
  );

  const breedDatabase = config.breed_database;

  const scoredSupplements: ScoredSupplement[] = useMemo(() => {
    if (!supplementRules?.supplements?.length) return [];
    return scoreSupplements(dogProfile, supplementRules, breedDatabase, config.exclusion_rules);
  }, [dogProfile, supplementRules, breedDatabase, config.exclusion_rules]);

  const strongIds = useMemo(() => {
    const ids = new Set<string>();
    scoredSupplements
      .filter((s) => s.isRecommended)
      .slice(0, 2)
      .forEach((s) => ids.add(s.supplement.id));
    return ids;
  }, [scoredSupplements]);

  const [showAll, setShowAll] = useState(false);
  const [modalScored, setModalScored] = useState<ScoredSupplement | null>(null);

  const allSupplements = useMemo(() => scoredSupplements.map((s) => s.supplement), [scoredSupplements]);

  const selectedIds = useMemo(
    () => new Set<string>(formData.selectedSupplements || []),
    [formData.selectedSupplements],
  );

  const selectedSupplementsMap: Record<string, boolean> = useMemo(() => {
    const map: Record<string, boolean> = {};
    scoredSupplements.forEach((s) => {
      map[s.supplement.id] = selectedIds.has(s.supplement.id);
    });
    return map;
  }, [scoredSupplements, selectedIds]);

  const persistSelection = useCallback(
    (nextIds: string[]) => {
      const uniqueIds = Array.from(new Set(nextIds));
      const validIds = uniqueIds.filter((id) => allSupplements.some((s) => s.id === id));
      const nextData = allSupplements.filter((s) => validIds.includes(s.id));
      const total = nextData.reduce((acc, s) => acc + s.price_eur, 0);

      updateFormData({
        selectedSupplements: validIds,
        selectedSupplementVariantIds: getSupplementVariantIds(validIds, allSupplements),
        selectedSupplementNames: nextData.map((s) => s.name),
        supplementsOneOffTotal: Number(total.toFixed(2)),
      });
    },
    [allSupplements, updateFormData],
  );

  useEffect(() => {
    if (isLoading) return;
    if (formData.selectedSupplements === undefined) return;
    const eligibleIds = new Set(allSupplements.map((supplement) => supplement.id));
    const validIds = formData.selectedSupplements.filter((id) => eligibleIds.has(id));
    if (validIds.length !== formData.selectedSupplements.length) persistSelection(validIds);
  }, [allSupplements, formData.selectedSupplements, isLoading, persistSelection]);

  const didAutoSelectRef = useRef(false);
  useEffect(() => {
    if (didAutoSelectRef.current) return;
    if (formData.selectedSupplements !== undefined) return;
    if (scoredSupplements.length === 0) return;
    const stronglyRecommendedIds = scoredSupplements
      .filter((s) => s.isRecommended && s.score >= STRONG_RECOMMENDATION_THRESHOLD)
      .map((s) => s.supplement.id);
    if (stronglyRecommendedIds.length === 0) return;
    didAutoSelectRef.current = true;
    persistSelection(stronglyRecommendedIds);
  }, [scoredSupplements, formData.selectedSupplements, persistSelection, STRONG_RECOMMENDATION_THRESHOLD]);

  const handleToggleSupplement = useCallback(
    (supplementId: string) => {
      const exists = allSupplements.some((s) => s.id === supplementId);
      if (!exists) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn(`[Step12Extras] toggle ignored — unknown supplement id: ${supplementId}`);
        }
        return;
      }
      const nextIds = selectedIds.has(supplementId)
        ? Array.from(selectedIds).filter((id) => id !== supplementId)
        : [...Array.from(selectedIds), supplementId];
      persistSelection(nextIds);
    },
    [allSupplements, selectedIds, persistSelection],
  );

  const visibleSupplements = showAll ? scoredSupplements : scoredSupplements.slice(0, INITIAL_VISIBLE);

  const hiddenCount = Math.max(0, scoredSupplements.length - INITIAL_VISIBLE);

  const handleContinuar = useCallback(() => {
    goToNextStep();
  }, [goToNextStep]);

  const handleContinuarSinExtras = useCallback(() => {
    persistSelection([]);
    goToNextStep();
  }, [persistSelection, goToNextStep]);

  const dogName = formData.nombre || 'tu perro';
  const selectedCount = selectedIds.size;

  const completedPhase2Steps = useMemo(() => {
    const base = [PHASE2_STEP_INDEX_RECETAS, PHASE2_STEP_INDEX_PLAN, PHASE2_STEP_INDEX_EXTRAS];
    if (formData.email) base.push(PHASE2_STEP_INDEX_CHECKOUT);
    return base;
  }, [formData.email]);

  const handleStepNavClick = useCallback(
    (stepIndex: number) => {
      const targetSlot: Record<number, number> = {
        [PHASE2_STEP_INDEX_RECETAS]: FUNNEL_SLOT_RECETAS,
        [PHASE2_STEP_INDEX_PLAN]: FUNNEL_SLOT_PLAN,
        [PHASE2_STEP_INDEX_CHECKOUT]: FUNNEL_SLOT_CHECKOUT,
      };
      const slot = targetSlot[stepIndex];
      if (slot !== undefined) setCurrentStep(slot);
    },
    [setCurrentStep],
  );

  return (
    <div className="min-h-screen bg-[#F7F4EE] flex flex-col">
      <Phase2Header step={4} />
      <StepNav
        currentStep={PHASE2_STEP_INDEX_EXTRAS}
        completedSteps={completedPhase2Steps}
        onStepClick={handleStepNavClick}
      />

      <div className="flex-1 px-4 py-6 funnel-bottom-clearance lg:pb-28 flex flex-col">
        <div className="max-w-3xl mx-auto w-full flex flex-col flex-1">
          <div className="text-center mb-5">
            <h1 className="font-fredoka text-2xl sm:text-3xl font-semibold text-[#1a1a1a]">
              {t('step12extras.title', 'Suplementos que recomendamos para {nombre}', { nombre: dogName })}
            </h1>
            <p className="font-poppins text-sm text-[#888] mt-1">
              {t('step12extras.subtitle', 'Puedes añadirlos al plan o continuar sin extras.', { nombre: dogName })}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3 mb-4">
            {visibleSupplements.map((scored) => {
              const supplement = scored.supplement;
              const isSelected = selectedSupplementsMap[supplement.id] ?? false;
              const imageUrl = supplement.image_url;

              return (
                <div
                  key={supplement.id}
                  className={`bg-white rounded-2xl flex flex-col transition-all cursor-pointer relative ${
                    isSelected
                      ? 'border-[3px] border-[#008BBC] shadow-lg ring-2 ring-[#008BBC]/20'
                      : 'border-2 border-[#e8e8e8] hover:border-[#ccc]'
                  }`}
                  onClick={() => handleToggleSupplement(supplement.id)}
                  role="button"
                  aria-pressed={isSelected}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleToggleSupplement(supplement.id);
                    }
                  }}
                >
                  {isSelected && (
                    <div
                      className="absolute top-2 right-2 w-6 h-6 rounded-full bg-[#008BBC] flex items-center justify-center shadow z-30"
                      aria-hidden="true"
                    >
                      <span className="text-white text-xs font-bold">✓</span>
                    </div>
                  )}

                  {scored.isRecommended && (
                    <div className="absolute -top-px left-1/2 z-30 flex -translate-x-1/2 -translate-y-1/2 pointer-events-none">
                      <RecommendationBadge
                        isRecommended={scored.isRecommended}
                        isStrong={strongIds.has(scored.supplement.id)}
                        strongLabel={t('step12extras.badge_strong', 'MUY RECOMENDADO')}
                        label={t('step12extras.badge_recommended', 'RECOMENDADO')}
                      />
                    </div>
                  )}

                  <div className="relative w-full h-32 sm:h-40 bg-white overflow-hidden rounded-t-2xl">
                    {imageUrl ? (
                      <img
                        src={imageUrl}
                        alt={supplementDisplayName(supplement)}
                        className="w-full h-full object-contain p-2"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-5xl">{supplement.emoji}</div>
                    )}

                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setModalScored(scored);
                      }}
                      aria-label={t('step12extras.info_aria', 'Ver detalle')}
                      className="absolute top-2 left-2 inline-flex items-center gap-1 p-1 sm:px-2 sm:py-1 bg-white hover:bg-gray-50 rounded-full border border-gray-200 shadow-sm z-10"
                    >
                      <Info className="h-4 w-4 text-[#008BBC]" strokeWidth={2.5} aria-hidden="true" />
                      <span className="hidden sm:inline font-poppins text-[#444] text-xs font-semibold">
                        {t('step12extras.info_label', 'Más info')}
                      </span>
                    </button>
                  </div>

                  <div className="p-3 flex flex-col gap-1 flex-1">
                    <h3 className="font-fredoka text-sm font-semibold text-[#1a1a1a] leading-tight line-clamp-1">
                      {supplementDisplayName(supplement)}
                    </h3>

                    {scored.caution ? (
                      <p className="font-poppins text-[11px] text-[#B45309] leading-snug line-clamp-2">
                        ⚠️ {scored.caution.message}
                      </p>
                    ) : scored.recommendationReasons.length > 0 ? (
                      <p className="font-poppins text-[11px] text-[#008BBC] leading-snug line-clamp-2 sm:line-clamp-1">
                        ✦ {scored.recommendationReasons[0]}
                      </p>
                    ) : (
                      <p className="font-poppins text-[11px] text-[#888] leading-snug line-clamp-2 sm:line-clamp-1">
                        {supplement.description}
                      </p>
                    )}

                    <div className="flex-1" />

                    <div className="flex items-center justify-between gap-2 mt-1.5">
                      <span className="font-fredoka text-sm sm:text-base font-black text-[#1a1a1a] flex-shrink-0">
                        {formatPrice(supplement.price_eur)}
                      </span>
                      {isSelected ? (
                        <span className="text-[11px] sm:text-sm font-bold px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-full bg-[#4caf50] text-white whitespace-nowrap shadow-sm">
                          {t('step12extras.added', '✓ AÑADIDO')}
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleSupplement(supplement.id);
                          }}
                          className="text-[11px] sm:text-sm font-bold px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-full bg-[#008BBC] text-white hover:bg-[#007aa8] transition-colors whitespace-nowrap"
                        >
                          {t('step12extras.add', 'AÑADIR')}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {!showAll && hiddenCount > 0 && (
            <div className="text-center mt-auto pt-6 mb-2">
              <button
                type="button"
                onClick={() => setShowAll(true)}
                className="font-poppins text-sm text-[#008BBC] font-medium hover:underline"
              >
                {t('step12extras.show_more', 'Ver {count} suplementos más ↓', { count: hiddenCount })}
              </button>
            </div>
          )}
        </div>
      </div>

      <FunnelStickyCTA
        label={
          selectedCount > 0 ? t('step12extras.cta', 'Continuar →') : t('step12extras.skip', 'Continuar sin extras →')
        }
        onClick={selectedCount > 0 ? handleContinuar : handleContinuarSinExtras}
        onBack={() => setCurrentStep(FUNNEL_SLOT_COMO_EMPEZAR)}
        backLabel={t('step12extras.back', 'Atrás')}
      />

      {modalScored && (
        <SupplementDetailModal
          scored={modalScored}
          onClose={() => setModalScored(null)}
          isSelected={selectedSupplementsMap[modalScored.supplement.id] ?? false}
          onAdd={() => {
            handleToggleSupplement(modalScored.supplement.id);
            setModalScored(null);
          }}
        />
      )}
    </div>
  );
}
